import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, AlertTriangle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Merge a duplicate record INTO a canonical one. Used by customers, vendors,
 * and locations — all three suffer from spelling-variant duplicates created
 * by auto-extract from invoices ("Mr Sandesh" vs "Sandesh Gottikere").
 *
 * Props
 *   open, onOpenChange      — controlled visibility
 *   sourceItem              — { id, name, ...meta } (the duplicate being merged AWAY)
 *   candidates              — full list of items of the same type. The dialog
 *                              filters out `sourceItem` and any record whose
 *                              `type` doesn't match the source's type (vendors
 *                              and locations only — customers ignore type).
 *   endpoint                — API path, e.g. `/api/pollen/customers/merge`
 *   entityLabel             — "customer" | "vendor" | "location" — used in copy
 *   itemMeta(item)          — optional renderer for the meta row beneath each
 *                              candidate's name (e.g. phone, type+location)
 *   matchKey(item)          — string used for the fuzzy match suggestion
 *                              ranking. Defaults to item.name
 *   onMerged()              — called on success so the parent can refresh
 */
const MergeIntoDialog = ({
  open,
  onOpenChange,
  sourceItem,
  candidates,
  endpoint,
  entityLabel = 'record',
  itemMeta,
  matchKey,
  onMerged,
  allowCrossType = false,  // true → vendor merge ignores type so a Transport
                           // can be merged into an Agent (and vice versa).
}) => {
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState('');
  const [busy, setBusy] = useState(false);

  // Score candidates by similarity to the source name. Records whose name
  // shares the most space-separated tokens with the source are surfaced
  // first — that's the typical duplicate pattern (e.g., "Sandesh G" vs
  // "Mr. Sandesh Gottikere"). Falls back to alphabetical when no source.
  const scored = useMemo(() => {
    if (!sourceItem) return [];
    const srcKey = (matchKey ? matchKey(sourceItem) : sourceItem.name || '').toLowerCase();
    const srcTokens = srcKey.split(/\s+/).filter(t => t.length > 1);
    const sourceType = sourceItem.type;

    const filtered = (candidates || []).filter(c => {
      if (!c || c.id === sourceItem.id) return false;
      // Same-type restriction — opt-out for vendor merges where a Transport
      // operator and an Agent record can legitimately be the same person.
      if (!allowCrossType && sourceType && c.type && c.type !== sourceType) return false;
      return true;
    });

    const ranked = filtered.map(c => {
      const cKey = (matchKey ? matchKey(c) : c.name || '').toLowerCase();
      const cTokens = cKey.split(/\s+/).filter(t => t.length > 1);
      const overlap = srcTokens.filter(t => cTokens.some(ct => ct.includes(t) || t.includes(ct))).length;
      const containsBoost = (cKey.includes(srcKey) || srcKey.includes(cKey)) ? 100 : 0;
      return { c, score: overlap * 10 + containsBoost };
    });
    ranked.sort((a, b) => b.score - a.score || (a.c.name || '').localeCompare(b.c.name || ''));
    return ranked.map(r => r.c);
  }, [candidates, sourceItem, matchKey, allowCrossType]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scored.slice(0, 50);
    return scored.filter(c => (c.name || '').toLowerCase().includes(q)).slice(0, 50);
  }, [scored, query]);

  const handleConfirm = async () => {
    if (!sourceItem || !pickedId) return;
    setBusy(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ canonical_id: pickedId, duplicate_ids: [sourceItem.id] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Merge failed');
      const moved = data.references_moved || {};
      const summary = Object.entries(moved)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
        .join(', ') || 'no embedded references';
      toast.success(`Merged ${entityLabel} \u00b7 ${summary}`);
      onOpenChange(false);
      setPickedId('');
      setQuery('');
      onMerged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!sourceItem) return null;
  const picked = scored.find(c => c.id === pickedId);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) { setPickedId(''); setQuery(''); } } }}>
      <DialogContent className="max-w-lg" data-testid={`merge-${entityLabel}-dialog`}>
        <DialogHeader>
          <DialogTitle>Merge {entityLabel}</DialogTitle>
          <DialogDescription>
            All references to <strong>{sourceItem.name}</strong> will be reassigned to the {entityLabel} you pick below, then this duplicate will be deleted. This is permanent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder={`Search ${entityLabel}s\u2026`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="merge-search-input"
            />
          </div>

          <div className="border rounded-lg max-h-72 overflow-y-auto divide-y" data-testid="merge-candidates">
            {visible.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No matching {entityLabel}s.
              </div>
            ) : visible.map(c => (
              <button
                key={c.id}
                onClick={() => setPickedId(c.id)}
                className={`w-full text-left p-3 hover:bg-muted transition-colors flex items-start gap-3 ${pickedId === c.id ? 'bg-amber-50 ring-1 ring-amber-300' : ''}`}
                data-testid={`merge-candidate-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  {itemMeta && <p className="text-[11px] text-muted-foreground truncate mt-0.5">{itemMeta(c)}</p>}
                </div>
              </button>
            ))}
          </div>

          {picked && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs flex items-start gap-2" data-testid="merge-preview">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="leading-relaxed">
                <strong>{sourceItem.name}</strong> <ArrowRight className="w-3 h-3 inline mx-1" /> <strong>{picked.name}</strong>
                <br />
                Every tour, payment, and reference pointing to <em>{sourceItem.name}</em> will be re-pointed to <em>{picked.name}</em>, then <em>{sourceItem.name}</em> will be deleted.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="merge-cancel-btn">Cancel</Button>
          <Button onClick={handleConfirm} disabled={!pickedId || busy} data-testid="merge-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {busy ? 'Merging\u2026' : `Merge into ${picked ? picked.name : '\u2026'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeIntoDialog;
